import { ApiPhotoResponse, Photo, LikeResponse } from '../../types/api';
import { getOrCreateGuestUserId } from '../user/guestUser';
import { apiClient, ApiError, NetworkError } from './apiClient';

/**
 * APIレスポンスをフロントエンド用の形式に変換
 */
function transformApiPhotoToPhoto(apiPhoto: ApiPhotoResponse): Photo {
  return {
    id: String(apiPhoto.pictureid),
    imageUrl: `data:image/jpeg;base64,${apiPhoto.base64}`,
    guestName: "ゲスト", // APIにuploader情報がないため固定値
    uploadDate: new Date().toISOString(), // APIにupload日時がないため現在時刻
    likes: apiPhoto.likesCount, // DBから取得したいいね数
    isLiked: apiPhoto.isLikedByCurrentUser, // APIから取得したいいね状態
    approved: apiPhoto.isModerated === 1,
    isModerated: apiPhoto.isModerated,
    moderatedAt: undefined,
    moderatedBy: undefined,
  };
}

/**
 * 全ての写真を取得（承認済みのみフィルタリング）
 * @param roomId - 取得対象のルームID
 */
export async function fetchApprovedPhotos(roomId: string): Promise<Photo[]> {
  try {
    // ゲストユーザーIDを取得または生成
    const guestUserId = getOrCreateGuestUserId();
    
    // クエリパラメータを構築
    const params = new URLSearchParams({
      roomId,
      guestUserId
    });
    
    // 統一APIクライアントを使用
    const apiPhotos = await apiClient.get<ApiPhotoResponse[]>(`/api/picture/fetchAllImages?${params.toString()}`);
    
    // 承認済み写真のみをフィルタリング（isModerated = 1 のみ）
    const filteredPhotos = apiPhotos.filter(photo => photo.isModerated === 1);
    
    // データ変換
    const transformedPhotos = filteredPhotos.map(transformApiPhotoToPhoto);
    
    // ソート（日付情報がないため、IDでソート）
    const approvedPhotos = transformedPhotos.sort((a, b) => parseInt(b.id) - parseInt(a.id));

    return approvedPhotos;
  } catch (error) {
    console.error('Failed to fetch approved photos:', error);
    throw error;
  }
}

/**
 * 写真にいいねを追加/削除
 */
export async function togglePhotoLike(photoId: string): Promise<{success: boolean, likesCount: number, message: string, isLikedByCurrentUser: boolean}> {
  try {
    console.log('[togglePhotoLike] Starting like toggle for photo:', photoId);
    
    // ゲストユーザーIDを取得または生成
    const guestUserId = getOrCreateGuestUserId();
    console.log('[togglePhotoLike] Using guestUserId:', guestUserId);
    
    const requestBody = { guestUserId };
    console.log('[togglePhotoLike] Request body:', requestBody);
    
    const response = await apiClient.post<{success: boolean, likesCount: number, message: string, isLikedByCurrentUser: boolean}>(
      `/api/picture/${photoId}/like`,
      requestBody
    );
    
    console.log('[togglePhotoLike] API response:', response);
    return response;
  } catch (error) {
    console.error('❌ [togglePhotoLike] Failed to toggle like:', error);
    throw error;
  }
}

/**
 * ユーザーのいいね状態を取得
 */
export async function fetchUserLikeStatus(photoIds: string[]): Promise<{ [photoId: string]: boolean }> {
  try {
    // バッチでいいね状態を取得（実装はAPIに依存）
    const response = await apiClient.post<{ [photoId: string]: boolean }>(
      '/api/picture/likes/status',
      { photo_ids: photoIds }
    );
    return response;
  } catch (error) {
    console.error('Failed to fetch like status:', error);
    return {};
  }
}

// 統一APIクライアントを再エクスポート（既存コードとの互換性）
export { apiClient, ApiError, NetworkError };